import React, {
  createContext,
  useCallback,
  useEffect,
  useState,
  useContext,
} from 'react'
import { Sheet, Box, ModalClose, Typography, Button, Modal } from '@mui/joy'
import Spinner from '../../shared/Spinner'
import parse from 'html-react-parser'
import Icon from '@mdi/react'
import { mdiCheckCircleOutline } from '@mdi/js'
import useTranslation from '../../shared/TranslationProvider'

const AlertContext = createContext()

export const AlertProvider = ({ children }) => {
  const [alertData, setData] = useState('')
  const [alertState, setState] = useState('')

  const setAlertData = useCallback(
    (data) => {
      setData(data)
    },
    [setData],
  )

  const setAlertState = useCallback(
    (state) => {
      setState(state)
    },
    [setState],
  )

  const clearAlert = useCallback(() => {
    setAlertState('')
    setAlertData('')
  }, [setAlertState, setAlertData])

  const value = {
    alertData,
    setAlertData,
    alertState,
    setAlertState,
    clearAlert,
  }

  return <AlertContext.Provider value={value}>{children}</AlertContext.Provider>
}

export const useAlert = () => useContext(AlertContext)

const ALERT_MODAL_STATES_WITH_DESC = ['alert', 'notice', 'success']

const MODAL_CLOSE_SX = {
  position: 'absolute',
  top: 8,
  right: 8,
  zIndex: 1,
  '--Icon-fontSize': '1.875rem',
}

const DESC_TYPOGRAPHY_SX = {
  a: { textDecoration: 'underline' },
  'a:hover': { textDecoration: 'none' },
}

export default function AlertModal() {
  const { alertData, alertState, clearAlert } = useAlert()
  const { t } = useTranslation()

  const describedBy = ALERT_MODAL_STATES_WITH_DESC.includes(alertState)
    ? 'modal-desc'
    : undefined

  // Focus #modal-desc when dialog opens so screen readers read the message before tabbing
  // to in-content links / Continue, then Close (last in DOM, visually top-right).
  useEffect(() => {
    if (alertState === '') return
    let cancelled = false
    const focusId = describedBy
      ? 'modal-desc'
      : alertState === 'loading'
        ? 'modal-title'
        : null
    if (!focusId) return
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return
        const el = document.getElementById(focusId)
        if (el && typeof el.focus === 'function') el.focus()
      })
    })
    return () => {
      cancelled = true
    }
  }, [alertState, alertData, describedBy])

  return (
    <>
      <Modal
        open={alertState !== ''}
        onClose={clearAlert}
        disableAutoFocus
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Sheet
          variant="outlined"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          aria-describedby={describedBy}
          aria-busy={alertState === 'loading' ? true : undefined}
          data-testid="a11y-logged-out-alert-modal"
          sx={{
            position: 'relative',
            maxWidth: '686px',
            minHeight: '200px',
            borderRadius: 'md',
            p: 3,
            boxShadow: 'lg',
            '--Typography-fontSize': '16px',
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            justifyContent: 'space-around',
          }}
        >
          {alertState === 'alert' && (
            <>
              <ModalHeader>{t('AlertModal.alert')}</ModalHeader>
              {/* CTS-9217 (YNS-015): links not identifiable by color alone — underline */}
              <Typography
                id="modal-desc"
                tabIndex={-1}
                className="outline-none focus:outline-none"
                data-testid="a11y-logged-out-alert-modal--desc"
                sx={DESC_TYPOGRAPHY_SX}
              >
                {alertData && parse(alertData)}
              </Typography>
            </>
          )}
          {alertState === 'notice' && (
            <>
              <ModalHeader>{t('AlertModal.notice')}</ModalHeader>
              <Typography
                id="modal-desc"
                tabIndex={-1}
                className="outline-none focus:outline-none"
                data-testid="a11y-logged-out-alert-modal--desc"
                sx={DESC_TYPOGRAPHY_SX}
              >
                {alertData && parse(alertData)}
              </Typography>
            </>
          )}
          {alertState === 'loading' && (
            <Box
              data-testid="a11y-logged-out-alert-modal--loading"
              sx={{
                width: '350px',
                height: '300px',
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                justifyContent: 'space-around',
              }}
            >
              <Spinner show />
              <ModalHeader
                tabIndex={-1}
                className="outline-none focus:outline-none"
              >
                {t('AlertModal.loading')}
              </ModalHeader>
            </Box>
          )}
          {alertState === 'success' && (
            <Box
              sx={{
                height: '300px',
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                justifyContent: 'space-around',
              }}
            >
              <Icon
                path={mdiCheckCircleOutline}
                color={'#123F1C'}
                size={6}
                aria-hidden
              />
              <ModalHeader>{t('AlertModal.success')}</ModalHeader>
              <Typography
                id="modal-desc"
                tabIndex={-1}
                className="outline-none focus:outline-none"
                data-testid="a11y-logged-out-alert-modal--desc"
              >
                {alertData}
              </Typography>
              <Button
                data-testid="a11y-logged-out-alert-modal--continue"
                sx={{
                  fontFamily: 'Roboto',
                  backgroundColor: '#123F1C',
                  padding: '0 24px',
                  color: '#F7FAFC',
                  fontSize: '20px',
                  fontStyle: 'normal',
                  fontWeight: '700',
                  lineHeight: '28px',
                  borderRadius: '20px',
                  height: '60px',
                  '&:hover': {
                    backgroundColor: '#08260C',
                  },
                }}
                size="lg"
                fullWidth
                onClick={clearAlert}
              >
                {t('AlertModal.continue')}
              </Button>
            </Box>
          )}
          {/* Last in tab order; visually top-right. CTS-9252 (YNS-016): close label */}
          {alertState !== '' && (
            <ModalClose
              variant="plain"
              color="black"
              size="xl"
              onClick={clearAlert}
              aria-label={t('AlertModal.close')}
              data-testid="a11y-logged-out-alert-modal--close"
              sx={MODAL_CLOSE_SX}
            />
          )}
        </Sheet>
      </Modal>
    </>
  )
}

function ModalHeader({
  children,
  level = 'h3',
  component = 'h2',
  fontWeight = 'xl',
  tabIndex: tabIndexProp,
  className,
  ...props
}) {
  return (
    <Typography
      component={component}
      id="modal-title"
      data-testid="a11y-logged-out-alert-modal--title"
      level={level}
      textColor="inherit"
      fontWeight={fontWeight}
      mb={1}
      tabIndex={tabIndexProp}
      className={className}
      sx={{
        color: 'var(--Gray-10, #2B2B2B)',
        fontFamily: 'Roboto',
        fontSize: '24px',
        fontWeight: '400',
        fontStyle: 'normal',
        lineHeight: 'normal',
        letterSpacing: '-0.48px',
      }}
      {...props}
    >
      {children}
    </Typography>
  )
}
